import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import {
  listBillingOrders,
  type OperatorBillingOrder,
  type OperatorBillingOrdersSummary,
} from '../../lib/operatorApi.js';
import { displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import { OperatorPage } from './OperatorShell.js';
import { OrgAvatar, WithAvatar } from './OperatorAvatars.js';

export function BillingHandoffPage() {
  const [orders, setOrders] = useState<readonly OperatorBillingOrder[] | null>(null);
  const [summary, setSummary] = useState<OperatorBillingOrdersSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const res = await listBillingOrders({
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      setOrders(res.orders);
      setSummary(res.summary);
    } catch {
      setError('دریافت لیست خریدها و تراکنش‌های درگاه پرداخت با خطا مواجه شد.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, [statusFilter]);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    if (!searchQuery.trim()) return orders;
    const q = searchQuery.trim().toLowerCase();
    return orders.filter(
      (o) =>
        o.organizationName.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q) ||
        o.ownerName.toLowerCase().includes(q) ||
        o.ownerContactLabel.toLowerCase().includes(q) ||
        (o.refId && o.refId.toLowerCase().includes(q)) ||
        (o.authority && o.authority.toLowerCase().includes(q)),
    );
  }, [orders, searchQuery]);

  return (
    <OperatorPage
      title="خریدها و ورود به درگاه پرداخت"
      actions={
        <Button variant="secondary" onClick={() => void loadData()} disabled={loading}>
          {loading ? 'در حال دریافت...' : 'به‌روزرسانی داده‌ها 🔄'}
        </Button>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {/* کارت‌های خلاصه آماری */}
      {summary ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <Card title="مجموع فروش موفق">
            <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginTop: 4 }}>
              {Number(summary.totalPaidAmountToman).toLocaleString('fa-IR')} تومان
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--sk-color-text-secondary, #6b7280)',
                marginTop: 4,
              }}
            >
              {Number(summary.totalPaidAmountRial).toLocaleString('fa-IR')} ریال
            </div>
          </Card>

          <Card title="تراکنش‌های موفق">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 24, fontWeight: 700 }}>
                {summary.paidOrders.toLocaleString('fa-IR')}
              </span>
              <span style={{ fontSize: 13, color: 'var(--sk-color-text-secondary, #6b7280)' }}>
                از مجموع {summary.totalOrders.toLocaleString('fa-IR')} سفارش
              </span>
            </div>
            <div style={{ marginTop: 8 }}>
              <Badge tone="success">پرداخت‌شده و فعال</Badge>
            </div>
          </Card>

          <Card title="در انتظار پرداخت / ورود به درگاه">
            <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b', marginTop: 4 }}>
              {summary.pendingOrders.toLocaleString('fa-IR')}
            </div>
            <div style={{ marginTop: 8 }}>
              <Badge tone="warning">ورود به درگاه زرین‌پال</Badge>
            </div>
          </Card>

          <Card title="ناموفق یا انصرافی">
            <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444', marginTop: 4 }}>
              {summary.failedOrders.toLocaleString('fa-IR')}
            </div>
            <div style={{ marginTop: 8 }}>
              <Badge tone="danger">عدم تکمیل / خطای درگاه</Badge>
            </div>
          </Card>
        </div>
      ) : null}

      {/* ابزارهای فیلتر و جستجو */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <FormField label="جستجوی خریدار یا سازمان">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="نام سازمان، شناسه، شماره تماس، کد پیگیری..."
          />
        </FormField>

        <FormField label="وضعیت تراکنش">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">همه تراکنش‌ها</option>
            <option value="paid">پرداخت موفق</option>
            <option value="pending">در انتظار درگاه / پرداخت</option>
            <option value="failed">ناموفق / انصرافی</option>
          </Select>
        </FormField>
      </div>

      {/* جدول داده‌ها */}
      <Card title="لیست سفارش‌ها و تراکنش‌های درگاه">
        {loading && !orders ? (
          <LoadingState />
        ) : filteredOrders.length === 0 ? (
          <EmptyState
            title="هیچ سفارشی یافت نشد"
            description="با فیلترهای انتخابی هیچ تراکنش خریدی ثبت نشده است."
          />
        ) : (
          <DataTable
            columns={[
              {
                key: 'date',
                header: 'زمان و تاریخ',
                render: (row) => (
                  <span style={{ fontSize: 13 }}>{displayJalaliDateTime(row.createdAt)}</span>
                ),
              },
              {
                key: 'organization',
                header: 'سازمان و شناسه',
                render: (row) => (
                  <WithAvatar avatar={<OrgAvatar slug={row.slug} name={row.organizationName} />}>
                    <div style={{ fontWeight: 600 }}>{row.organizationName}</div>
                    <Link
                      to={`/internal/admin/organizations/${encodeURIComponent(row.slug)}`}
                      style={{ fontSize: 12, fontFamily: 'monospace', textDecoration: 'none' }}
                    >
                      {row.slug} ↗
                    </Link>
                  </WithAvatar>
                ),
              },
              {
                key: 'buyer',
                header: 'خریدار / کاربر',
                render: (row) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{row.ownerName}</div>
                    <div
                      dir="ltr"
                      style={{
                        fontSize: 12,
                        fontFamily: 'monospace',
                        color: 'var(--sk-color-text-secondary, #6b7280)',
                      }}
                    >
                      {row.ownerContactLabel}
                    </div>
                  </div>
                ),
              },
              {
                key: 'plan',
                header: 'طرح و مشخصات',
                render: (row) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{row.planName || 'طرح استاندارد'}</div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--sk-color-text-secondary, #6b7280)',
                      }}
                    >
                      {row.usersCount} کاربر • {row.durationMonths} ماهه
                    </div>
                  </div>
                ),
              },
              {
                key: 'amount',
                header: 'مبلغ پرداختی',
                render: (row) => (
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      {Number(row.amountToman).toLocaleString('fa-IR')} تومان
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--sk-color-text-secondary, #6b7280)',
                      }}
                    >
                      {Number(row.amountRial).toLocaleString('fa-IR')} ریال
                    </div>
                  </div>
                ),
              },
              {
                key: 'status',
                header: 'وضعیت درگاه',
                render: (row) => {
                  if (row.status === 'paid' || row.status === 'provisioned') {
                    return <Badge tone="success">پرداخت موفق</Badge>;
                  }
                  if (row.status === 'pending') {
                    return <Badge tone="warning">ورود به درگاه</Badge>;
                  }
                  return <Badge tone="danger">ناموفق / انصراف</Badge>;
                },
              },
              {
                key: 'gatewayDetails',
                header: 'مشخصات تراکنش بانکی',
                render: (row) => (
                  <div dir="ltr" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                    {row.refId ? (
                      <div>
                        <strong>Ref:</strong> {row.refId}
                      </div>
                    ) : null}
                    {row.cardPan ? (
                      <div>
                        <strong>کارت:</strong> {row.cardPan}
                      </div>
                    ) : null}
                    {row.authority ? (
                      <div
                        title={row.authority}
                        style={{ color: 'var(--sk-color-text-secondary, #6b7280)' }}
                      >
                        Auth: {row.authority.slice(0, 14)}...
                      </div>
                    ) : null}
                    {row.failureReason ? (
                      <div
                        dir="rtl"
                        style={{ color: '#ef4444', fontFamily: 'inherit', marginTop: 2 }}
                      >
                        {row.failureReason}
                      </div>
                    ) : null}
                    {!row.refId && !row.authority && !row.failureReason ? <span>—</span> : null}
                  </div>
                ),
              },
            ]}
            rows={[...filteredOrders]}
            rowKey={(row) => row.orderId}
          />
        )}
      </Card>
    </OperatorPage>
  );
}

export { BillingHandoffPage as BillingOrdersPage };
